import React, { useState, useEffect } from 'react';
import { X, Sparkles, CreditCard, Globe, Tag, Truck } from 'lucide-react';

// Current app version - update this with each release
const APP_VERSION = '1.6.0';

const CHANGELOG = [
  {
    version: '1.6.0',
    date: 'July 2026',
    title: "What's New in SALESSPACE",
    features: [
      {
        icon: Sparkles,
        title: 'Smoother Updates',
        description: 'App now prompts you before updating, so you never lose your work mid-checkout.'
      },
      {
        icon: CreditCard,
        title: 'Paystack Payments',
        description: 'Now accepting payments in Nigeria, Ghana, South Africa & Kenya with local currency support.'
      },
      {
        icon: Tag,
        title: 'Shop by Category',
        description: 'Browse products faster with our new category filters - Electronics, Fashion, Home & more.'
      },
      {
        icon: Truck,
        title: 'Order Tracking',
        description: 'Track your orders in real-time from purchase to delivery at /track.'
      },
      {
        icon: Globe,
        title: 'Multi-Currency Pricing',
        description: 'See prices in your local currency - EUR, NGN, GBP, ZAR, and more supported.'
      }
    ]
  }
];

const STORAGE_KEY = 'salesspace_last_seen_version';

// Routes where modal should NOT appear (auth/onboarding pages)
const EXCLUDED_ROUTES = [
  '/admin/login',
  '/merchant/login',
  '/merchant/register'
];

export default function WhatsNewModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentChangelog, setCurrentChangelog] = useState(null);

  useEffect(() => {
    // Don't show on auth/onboarding pages
    const currentPath = window.location.pathname;
    if (EXCLUDED_ROUTES.some(route => currentPath.startsWith(route))) {
      return;
    }

    const lastSeenVersion = localStorage.getItem(STORAGE_KEY);
    
    // Show modal if:
    // 1. User has never seen any version (first time user after feature launched)
    // 2. User has seen an older version
    if (!lastSeenVersion || lastSeenVersion !== APP_VERSION) {
      const changelog = CHANGELOG.find(c => c.version === APP_VERSION);
      if (changelog) {
        setCurrentChangelog(changelog);
        // Small delay to let the page render first
        setTimeout(() => setIsOpen(true), 1000);
      }
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, APP_VERSION);
    setIsOpen(false);
  };

  if (!isOpen || !currentChangelog) return null;

  return (
    <div 
      data-testid="whats-new-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={handleClose}
    >
      <div 
        data-testid="whats-new-modal"
        className="relative w-full max-w-md bg-gradient-to-b from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
          <button
            data-testid="whats-new-close"
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{currentChangelog.title}</h2>
              <p className="text-sm text-slate-400">Version {currentChangelog.version} • {currentChangelog.date}</p>
            </div>
          </div>
        </div>

        {/* Features list */}
        <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
          <div className="space-y-4">
            {currentChangelog.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="flex gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors"
                >
                  <div className="flex-shrink-0 p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg">
                    <Icon className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm">{feature.title}</h3>
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2">
          <button
            data-testid="whats-new-got-it"
            onClick={handleClose}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25"
          >
            Got it, let&apos;s shop!
          </button>
          <p className="text-center text-xs text-slate-500 mt-3">
            Thanks for using SALESSPACE ✨
          </p>
        </div>
      </div>
    </div>
  );
}

// Export version for other components if needed
export { APP_VERSION };
